import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Dialog, DialogContent, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import type { DashboardAnnouncementClient } from "../services/api/dashboardAnnouncements";

const INTERNAL_HREF_ALLOWLIST = new Set([
  "/",
  "/create-notice",
  "/view-notices",
  "/calendar",
  "/user-management",
  "/circles",
  "/team-management",
  "/library",
  "/drive",
  "/help/videos",
  "/ad-center",
  "/profile",
]);

function resolveOrgId(user: ReturnType<typeof useAuth>["user"]): string | null {
  if (!user) return null;
  return (user.schoolId || user.orgId || null) as string | null;
}

function isHttpsExternal(href: string): boolean {
  try {
    const url = new URL(href);
    return url.protocol === "https:";
  } catch {
    return false;
  }
}

function isHttpsImage(url?: string): boolean {
  if (!url) return false;
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return url.startsWith("https://");
  }
}

/**
 * Operator popup (modal) in the authenticated Dashboard shell.
 * Waits if the create-notice wizard is already open.
 */
const AnnouncementHost: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const orgId = resolveOrgId(user);
  const createWizardOpen = location.pathname === "/create-notice";

  const [optimisticAcked, setOptimisticAcked] = useState<Set<string>>(
    () => new Set()
  );
  const [sessionAnnouncement, setSessionAnnouncement] =
    useState<DashboardAnnouncementClient | null>(null);
  const [imageReady, setImageReady] = useState(false);
  const seenSent = useRef<Set<string>>(new Set());

  const { data } = useQuery({
    queryKey: ["dashboard-announcements", orgId],
    queryFn: async () => {
      if (!orgId || !token) throw new Error("Missing org or token");
      return api.dashboardAnnouncements.list(orgId, token);
    },
    enabled: !!orgId && !!token,
    staleTime: 0,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const ackMutation = useMutation({
    mutationFn: async ({
      announcementId,
      action,
    }: {
      announcementId: string;
      action: "seen" | "click" | "dismiss";
    }) => {
      if (!orgId || !token) throw new Error("Missing org or token");
      return api.dashboardAnnouncements.ack(
        orgId,
        announcementId,
        token,
        action
      );
    },
    onSuccess: (_res, variables) => {
      if (variables.action === "seen") return;
      setOptimisticAcked((prev) => new Set(prev).add(variables.announcementId));
      // Backlog drops resolve while the top popup is still open — don't close it.
      setSessionAnnouncement((current) =>
        current?.announcementId === variables.announcementId ? null : current
      );
      queryClient.invalidateQueries({
        queryKey: ["dashboard-announcements", orgId],
      });
    },
  });

  const pending = useMemo(() => {
    const list = data?.announcements || [];
    return list
      .filter((a) => !optimisticAcked.has(a.announcementId))
      .slice()
      .sort(
        (a, b) =>
          b.priority - a.priority ||
          a.announcementId.localeCompare(b.announcementId)
      );
  }, [data, optimisticAcked]);

  useEffect(() => {
    if (createWizardOpen) return;
    if (!sessionAnnouncement && pending[0]) {
      setSessionAnnouncement(pending[0]);
    }
  }, [pending, createWizardOpen, sessionAnnouncement]);

  useEffect(() => {
    const url = sessionAnnouncement?.imageUrl;
    if (!sessionAnnouncement) {
      setImageReady(false);
      return;
    }
    if (!isHttpsImage(url)) {
      setImageReady(true);
      return;
    }
    let cancelled = false;
    setImageReady(false);
    const img = new Image();
    const finish = () => {
      if (!cancelled) setImageReady(true);
    };
    img.onload = finish;
    img.onerror = finish;
    img.src = url as string;
    const timeout = window.setTimeout(finish, 8000);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      img.onload = null;
      img.onerror = null;
    };
  }, [sessionAnnouncement]);

  const activeModal =
    createWizardOpen || !imageReady ? null : sessionAnnouncement;

  const track = useCallback(
    (
      announcement: DashboardAnnouncementClient,
      action: "seen" | "click" | "dismiss"
    ) => {
      ackMutation.mutate({
        announcementId: announcement.announcementId,
        action,
      });
    },
    [ackMutation]
  );

  useEffect(() => {
    if (!activeModal || !orgId || !token) return;
    const id = `${activeModal.announcementId}:${activeModal.sendGeneration ?? 0}`;
    if (seenSent.current.has(id)) return;
    seenSent.current.add(id);
    track(activeModal, "seen");

    // Only the top popup is shown. Anything else that piled up (e.g. the user
    // hasn't logged in for a while) is dropped, not queued one after another.
    const backlog = pending.filter(
      (a) => a.announcementId !== activeModal.announcementId
    );
    if (backlog.length === 0) return;
    setOptimisticAcked((prev) => {
      const next = new Set(prev);
      backlog.forEach((a) => next.add(a.announcementId));
      return next;
    });
    backlog.forEach((a) => track(a, "dismiss"));
  }, [activeModal, pending, orgId, token, track]);

  const dismiss = useCallback(
    (announcement: DashboardAnnouncementClient) => {
      setSessionAnnouncement(null);
      setOptimisticAcked((prev) => new Set(prev).add(announcement.announcementId));
      track(announcement, "dismiss");
    },
    [track]
  );

  const runCta = useCallback(
    (announcement: DashboardAnnouncementClient) => {
      const href = announcement.cta?.href?.trim();
      if (href) {
        if (INTERNAL_HREF_ALLOWLIST.has(href.split("?")[0].split("#")[0])) {
          navigate(href);
        } else if (isHttpsExternal(href)) {
          window.open(href, "_blank", "noopener,noreferrer");
        }
      }
      track(announcement, "click");
    },
    [track, navigate]
  );

  return (
    <Dialog
      open={Boolean(activeModal)}
      onClose={() => activeModal && dismiss(activeModal)}
      maxWidth={false}
      aria-label="Noticeboard message"
      PaperProps={{
        sx: {
          width: "fit-content",
          maxWidth: "90vw",
          maxHeight: "92vh",
          bgcolor: "transparent",
          boxShadow: "none",
          overflow: "hidden",
          borderRadius: 2,
        },
      }}
    >
      {activeModal && (
        <DialogContent sx={{ p: 0, position: "relative", overflow: "hidden" }}>
          <IconButton
            onClick={() => dismiss(activeModal)}
            aria-label="Dismiss"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
              bgcolor: "rgba(0,0,0,0.45)",
              color: "common.white",
              "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {isHttpsImage(activeModal.imageUrl) && (
            <Box
              component="img"
              src={activeModal.imageUrl}
              alt=""
              decoding="sync"
              onClick={
                activeModal.cta ? () => runCta(activeModal) : undefined
              }
              sx={{
                display: "block",
                width: "auto",
                height: "auto",
                maxWidth: "90vw",
                maxHeight: "90vh",
                cursor: activeModal.cta ? "pointer" : "default",
              }}
            />
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default AnnouncementHost;
